import Minze, { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import MainModule from '../../modules/main'
import { encodeSlides, LvcPrimarySliderItem } from '../../modules/sliderUtils'
import {
  AbstractSearchCore,
  MultipleResults,
  ISearchConfig,
  IFacet,
  AbstractSubscriber,
} from '../../modules/search'
import { mapEvent } from '../../modules/utils'
import { Locale } from '../../modules/locale'
import { getSlides } from './functions'
import { config } from '../../constants'

Locale.getInstance().registerKeySet('fr', 'freeSlider', {
  title: 'Gratuit au Québec',
})

Locale.getInstance().registerKeySet('en', 'freeSlider', {
  title: 'Free in Quebec',
})

export interface LvcFreeSlider {
  items: any[]
  total: number
}

export class FreeSliderSubscriber extends AbstractSubscriber {
  private readonly index: string = 'eventsFree'
  private _core: AbstractSearchCore | undefined
  private _initialized = false
  private searchConfig: ISearchConfig = {}
  private now = new Date()
  private nowUnix = Math.floor(this.now.getTime() / 1000)
  private unixOffset = config.slidersConfigs.free.maxDaysToFilter * 24 * 60 * 60
  private dateFilters = [this.nowUnix, this.nowUnix + this.unixOffset]

  constructor() {
    super('eventsFree')
    let mainModule = MainModule.getInstance()

    mainModule.getSearchManager().subscribe(this)
  }

  async attached(searchCore: AbstractSearchCore): Promise<void> {
    this._core = searchCore
    this._initialized = true
  }

  detaching(): void {}

  async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {}

  async onSearchTextUpdate(text: string): Promise<void> {}

  async onFacetUpdate(facet: IFacet[]): Promise<void> {}

  async onResultUpdate(results: MultipleResults): Promise<void> {}

  async goToSearch() {
    this._core?.applySearchConfig(this.searchConfig)
    this._core?.goToSearch()
  }

  public async applyFacetsAndExecuteSearch(): Promise<{ items: any; total: number }> {
    this.searchConfig = {
      ...this.searchConfig,
      facets: config.slidersConfigs.free.facets,
      dateFilters: this.dateFilters,
      hitsPerPage: config.slidersConfigs.free.hitsPerPage,
    }
    let response: MultipleResults = (await this._core?.executeSearch(
      this.searchConfig
    )) as MultipleResults

    return {
      items: response?.events_search?.items || [],
      total: response?.events_search?.total || 0,
    }
  }
}

//////////////////////////////////////////////////////////////////////////////////////

export class LvcFreeSlider extends MinzeElement {
  attrs: Attrs = [['layout', 'grid']]
  reactive: Reactive = ['items', 'total']
  private subscriber: FreeSliderSubscriber = new FreeSliderSubscriber()
  isMobile = window.matchMedia('(max-width: 730px)').matches

  onReady = () => {
    this.loadEvents()
  }

  loadEvents = async () => {
    let items: LvcPrimarySliderItem[] = []
    const eventsResult = await this.subscriber.applyFacetsAndExecuteSearch()
    this.total = eventsResult.total
    let events = eventsResult.items
    if (events) {
      items = events.map((event: any) => mapEvent(event))
      this.items = items
    } else {
      this.items = []
    }

    this.rerender()
    Minze.cast('minze:lvc-free-slider-ready', {
      tagName: 'lvc-free-slider',
      title: Locale.getKey('freeSlider.title'),
      total: this.total,
    })
  }

  viewMore = async () => {
    await this.subscriber.goToSearch()
  }

  html = () => {
    if (!this.items || (this.items as any[]).length === 0) {
      return ``
    }
    return /*html*/ `
      <lvc-grid-layout data-component-location="activity_pack" data="${encodeSlides(this.items.filter(item => item).slice(0, 14))}"></lvc-grid-layout>
      <div class="view-more-layout">
        <div id="view-more">
          <div class="view-more-text">${Locale.getKey('lvc-activity-pack.viewMore', { title: Locale.getKey('freeSlider.title') })}</div>
          <div class="view-more-icon-wrapper">
            <lvc-arrow-right-icon></lvc-arrow-right-icon>
          </div>
        </div>
      </div>
      <lvc-slider-title text="${Locale.getKey('freeSlider.title')}" counter="${this.total}"></lvc-slider-title>
      <lvc-primary-slider
        data-component-location="activity_pack"
        list-name="${Locale.getKey('freeSlider.title')}"
        items-per-slide="3"
        fade-effect="true"
        data-slides="${encodeSlides(getSlides(this.items, this.total, 'lvc:free-slider-see-more'))}"
        style="margin-bottom: 1rem;"
        ></lvc-primary-slider> 
  `
  }
  css = () => `
      :host {
        ${!this.items || (this.items as any[]).length === 0 ? 'display: none;' : ''}
      }
      /* @media screen and (min-width: 600px) {
        lvc-primary-slider {
          height: 450px;
        }
      } */
      lvc-grid-layout {
        display: ${!this.layout || this.layout === 'grid' ? 'grid' : 'none'};
      }
      lvc-primary-slider {
        display: ${this.layout === 'slider' ? 'block' : 'none'};
      }
      .view-more-layout {
        display: ${this.layout === 'grid' ? 'flex' : 'none'};
        margin: auto;
        justify-content: center;
        margin-top: 60px;
      }
      #view-more {
        font-size: 1.5rem;
        font-weight: 500;
        color: #1E007D;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        gap: 20px;
        cursor: pointer;
      }
      .view-more-icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        background-color: #ffffff;
        border-radius: 100%;
      }
      lvc-arrow-right-icon {
        width: 35px;
        height: 35px;
        transform: rotate(-25deg);
      }
      .view-more-text {
        border-bottom: 1px solid #1E007D;
      }
      .title-container {
        display: ${!this.layout || this.layout === 'grid' ? 'none' : 'flex'};
        align-items: center;
        column-gap: 10px;
        cursor: pointer;
      }
      @media only screen and (max-width: 991px) {
        lvc-grid-layout {
          display: none;
        }
        lvc-primary-slider {
          display: block;
        }
        .title-container {
          display: flex;
        }
        .view-more-layout {
          display: none;
        }
      }
      .counter {
        display: inline-flex;
        height: 34px;
        min-width: 34px;
        padding: 7px;
        justify-content: center;
        align-items: center;
        border-radius: 40px;
        background: #1E007D;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: 120%;
        color: #FF4600;
        position: relative;
        top: 2px;
      }
  `
  eventListeners: EventListeners = [
    [window, 'lvc:free-slider-see-more', this.viewMore],
    ['.title-container', 'click', this.viewMore],
    ['#view-more', 'click', this.viewMore],
  ]
}
